import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import type { Event, EventPayload } from './events.js'
import * as PostHog from './posthog.js'

type Empty = Record<string, never>

const AnalyticsContext = createContext<Record<string, unknown>>({})

/** Provides default properties that are merged into every analytics event captured by descendants. */
export function AnalyticsProvider(props: AnalyticsProvider.Props) {
  const parent = useContext(AnalyticsContext)
  const key = JSON.stringify(PostHog.sanitizeProperties(props.properties))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const merged = useMemo(() => ({ ...parent, ...props.properties }), [parent, key])
  return <AnalyticsContext.Provider value={merged}>{props.children}</AnalyticsContext.Provider>
}

export declare namespace AnalyticsProvider {
  type Props = {
    /** Default properties merged into every event. */
    properties: Record<string, unknown>
    children: ReactNode
  }
}

/** Returns a type-safe `capture` function that auto-merges context properties from {@link AnalyticsProvider}. */
export function useAnalytics() {
  const defaults = useContext(AnalyticsContext)
  const capture = useCallback(
    <const event extends Event>(
      event: event,
      ...args: Empty extends EventPayload[event]
        ? [properties?: EventPayload[event]]
        : [properties: EventPayload[event]]
    ) => {
      PostHog.capture(event, args[0] ?? {}, defaults)
    },
    [defaults],
  )
  return { capture, identify: PostHog.identify, reset: PostHog.reset }
}
